<div class="app-frame" [style.display]="authenticated() || onOAuthCallback() ? null : 'none'"
     [class.app-frame--assistant-docked]="assistantDocked()"
     [style.--assistant-bottom-clearance]="assistantBottomClearance() + 'px'">
  <div class="app-shell" [class.app-shell--oauth-callback]="onOAuthCallback()"
       [class.app-shell--mobile-open]="mobileOpen()"
       [class.app-shell--collapsed]="navCollapsed()">
    <a class="skip-link" href="#main-content">Skip to main content</a>

    <div class="usa-banner app-shell__banner" aria-label="Official website banner">
      <div class="usa-banner__inner">
        <div class="usa-banner__lockup">
        <!--
          The flag, per design.cms.gov's usa-banner. It was a blue box containing the letters "US" - a
          placeholder that read as a label rather than a flag.

          Inline rather than an <app-icon>, because IconComponent renders a square 24x24 viewBox and a flag is
          roughly 1.9:1; routing it through there would have squashed it. Inline rather than an image file so the
          banner needs no asset request to render correctly on first paint.

          Thirteen stripes and a canton, in the flag's own colours. The stars are dots on purpose: at 19x10 a
          fifty-star field is smaller than a pixel per star, and the reference image at this size shows the same
          suggestion of a field rather than countable stars.
        -->
        <svg class="usa-banner__flag" viewBox="0 0 19 10" aria-hidden="true" focusable="false">
          <rect width="19" height="10" fill="#b31942" />
          <g fill="#fff">
            <rect y="0.77" width="19" height="0.77" />
            <rect y="2.31" width="19" height="0.77" />
            <rect y="3.85" width="19" height="0.77" />
            <rect y="5.38" width="19" height="0.77" />
            <rect y="6.92" width="19" height="0.77" />
            <rect y="8.46" width="19" height="0.77" />
          </g>
          <rect width="8.4" height="5.38" fill="#0a3161" />
          <g fill="#fff">
            <circle cx="1.4" cy="1" r="0.32" />
            <circle cx="3.2" cy="1" r="0.32" />
            <circle cx="5" cy="1" r="0.32" />
            <circle cx="6.8" cy="1" r="0.32" />
            <circle cx="2.3" cy="2.2" r="0.32" />
            <circle cx="4.1" cy="2.2" r="0.32" />
            <circle cx="5.9" cy="2.2" r="0.32" />
            <circle cx="1.4" cy="3.4" r="0.32" />
            <circle cx="3.2" cy="3.4" r="0.32" />
            <circle cx="5" cy="3.4" r="0.32" />
            <circle cx="6.8" cy="3.4" r="0.32" />
            <circle cx="2.3" cy="4.6" r="0.32" />
            <circle cx="4.1" cy="4.6" r="0.32" />
            <circle cx="5.9" cy="4.6" r="0.32" />
          </g>
        </svg>
        <span>An official website of the United States government</span>
        </div>
        <button class="usa-banner__link"
                type="button"
                aria-controls="usa-banner-guidance"
                [attr.aria-expanded]="usaBannerExpanded()"
                (click)="toggleUsaBanner()">
          <span>Here’s how you know</span>
          <span class="usa-banner__chevron" [class.usa-banner__chevron--open]="usaBannerExpanded()" aria-hidden="true"></span>
        </button>
      </div>

      @if (usaBannerExpanded()) {
        <div id="usa-banner-guidance" class="usa-banner__guidance">
          <section class="usa-banner__guidance-item">
            <span class="usa-banner__guidance-icon usa-banner__guidance-icon--gov" aria-hidden="true">🏛</span>
            <div>
              <strong>Official websites use .gov</strong>
              <p>A <strong>.gov</strong> website belongs to an official government<br class="usa-banner__desktop-break"> organization in the United States.</p>
            </div>
          </section>
          <section class="usa-banner__guidance-item">
            <span class="usa-banner__guidance-icon usa-banner__guidance-icon--secure" aria-hidden="true">🔒</span>
            <div>
              <strong>Secure .gov websites use HTTPS</strong>
              <p>A <strong>lock (🔒)</strong> or <strong>https://</strong> means you’ve safely connected to the .gov website. Share sensitive information only on official, secure websites.</p>
            </div>
          </section>
        </div>
      }
    </div>

    <header class="app-header app-shell__header">
      <button
        class="icon-button app-header__menu"
        type="button"
        aria-label="Toggle navigation"
        aria-controls="primary-navigation"
        [attr.aria-expanded]="!navCollapsed()"
        (click)="toggleNavigation()"
      >
        <app-icon name="menu" />
      </button>

      <a class="app-header__brand" [routerLink]="[roleBasePath(), 'dashboard']" (click)="closeNavigation()">
        <!-- Sized with the wordmark it sits beside; 22px left the mark looking incidental next to it. -->
        <app-icon name="hub-mark" [size]="28" />
        <span>Hub Central</span>
      </a>

      <!-- Beside the wordmark rather than on one screen: it re-keys the data on every partner-scoped
           page, so it has to be reachable from all of them. Renders only for a multi-organization
           account. -->
      <app-partner-switcher />

      <div class="app-header__profile">
        <button
          class="app-header__profile-trigger"
          type="button"
          aria-haspopup="menu"
          [attr.aria-expanded]="profileOpen()"
          aria-controls="account-menu"
          (click)="toggleProfileMenu($event)"
        >
          <span class="app-header__avatar">{{ signedInInitials() }}</span>
          <span class="app-header__name truncate-value" appOverflowTooltip>{{ signedInName() }}</span>
          <app-icon
            class="app-header__chevron"
            [class.app-header__chevron--open]="profileOpen()"
            name="chevron-down"
            [size]="14"
          />
        </button>

        @if (profileOpen()) {
          <div id="account-menu" class="account-menu" role="menu">
            <div class="account-menu__identity" aria-hidden="true">
              <strong>{{ signedInName() }}</strong>
            </div>

            <!--
              A switch, not a menu item: the state is the point, so it has to be readable without being
              clicked. A native checkbox carries that state for assistive technology for free - role="switch"
              on a button would need aria-checked maintained by hand - and the label wrapping it makes the
              whole row, text included, the hit target.

              menuitemcheckbox is the ARIA role for a checkable entry inside role="menu"; a bare checkbox
              would be an invalid child of one.
            -->
            <label class="account-menu__item account-menu__toggle" role="menuitemcheckbox"
                   [attr.aria-checked]="darkTheme()">
              <app-icon [name]="darkTheme() ? 'moon' : 'sun'" [size]="18" />
              <span>Dark theme</span>
              <span class="switch">
                <input class="switch__input" type="checkbox" [checked]="darkTheme()" (change)="toggleTheme($event)" />
                <span class="switch__track" aria-hidden="true"></span>
              </span>
            </label>

            <button
              class="account-menu__item account-menu__item--signout"
              type="button"
              role="menuitem"
              [disabled]="signingOut()"
              (click)="signOut()"
            >
              <app-icon name="logout" [size]="18" />
              <span>{{ signingOut() ? 'Signing out…' : 'Sign out' }}</span>
            </button>
          </div>
        }
      </div>
    </header>

    <!-- No brand block here: the header already carries it, and repeating it cost a row of vertical space
         while saying nothing new. Keeping it in the header also means the product name stays on screen
         when this sidebar is collapsed to icons. -->
    <aside class="sidebar app-shell__sidebar">
      <nav id="primary-navigation" class="sidebar__nav" aria-label="Primary navigation">
        @for (item of roleConfig().nav; track item.path) {
          <!-- title and aria-label carry the destination when collapsed: the visual label is clipped and
               faded during the width transition, while the explicit accessible name remains stable. -->
          <a
            class="sidebar__item"
            [routerLink]="[roleBasePath(), item.path]"
            routerLinkActive="sidebar__item--active"
            ariaCurrentWhenActive="page"
            [attr.title]="item.label"
            [attr.aria-label]="item.label"
            (click)="closeNavigation()"
          >
            <app-icon class="sidebar__icon" [name]="item.icon" />
            <span class="sidebar__label">{{ item.label }}</span>
          </a>
        }
      </nav>

      <!-- A second way to collapse, at the bottom of the menu where the pattern is established. The
           hamburger in the header does the same thing, but nothing up there says what it affects; this one
           names the action. The chevrons flip so the control describes what the next click will do, and
           the label follows the same hide-when-collapsed rule as the navigation labels above. -->
      <button
        class="sidebar__collapse"
        type="button"
        [attr.title]="navCollapsed() ? 'Expand menu' : 'Collapse menu'"
        [attr.aria-label]="navCollapsed() ? 'Expand menu' : 'Collapse menu'"
        [attr.aria-expanded]="!navCollapsed()"
        aria-controls="primary-navigation"
        (click)="toggleNavigation()"
      >
        <app-icon class="sidebar__icon" [name]="navCollapsed() ? 'chevrons-right' : 'chevrons-left'" />
        <span class="sidebar__label">Collapse</span>
      </button>
    </aside>

    <button class="sidebar-scrim" type="button" aria-label="Close navigation" (click)="closeNavigation()"></button>

    <main #mainContent id="main-content" class="app-shell__main"
          [class.app-shell__main--user]="userFlow()"
          [class.app-shell__main--approver]="approverFlow()"
          [class.app-shell__main--admin]="adminFlow()"
          [class.app-shell__main--assistant-docked]="assistantDocked()">
      @if (userProfile.error(); as profileError) {
        <app-banner
          variant="danger"
          title="We couldn’t load your profile"
          [message]="profileError.message"
          [reference]="profileError.reference"
          [dismissible]="true"
          (dismissed)="userProfile.dismissError()"
        />
      }
      <router-outlet />
    </main>

  </div>
</div>

@if (assistantRuntime.enabled()) {
  <app-assistant-panel [authenticated]="assistantAuthenticated()"
                       [dockArea]="assistantDocked() ? mainContent : null"
                       (dockClearanceChange)="assistantBottomClearance.set($event)" />
}

<!-- Session-expiry warning. Driven by SessionTimeoutService: it opens two minutes before the access
     token lapses, counts down, and signs out at zero unless the user chooses to stay. Placed outside
     .app-frame so the frame's display:none (while the session is being checked) can never hide it. The
     scrim is inert on purpose - this is a deliberate choice, not something to dismiss by clicking away. -->
@if (session.warningOpen()) {
  <div class="modal-scrim"></div>
  <div
    class="modal modal--warning"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="session-timeout-title"
    aria-describedby="session-timeout-message"
  >
    <h2 class="modal__title" id="session-timeout-title">Your session is about to expire</h2>
    <p class="modal__detail" id="session-timeout-message">
      For your security, you’ll be signed out in {{ session.remainingLabel() }}. Do you want to stay signed in?
    </p>
    <div class="modal__actions">
      <button class="button button--secondary" type="button" (click)="session.logout()">Sign out now</button>
      <button
        class="button button--primary"
        type="button"
        appAutofocus
        [disabled]="session.extending()"
        (click)="session.staySignedIn()"
      >
        {{ session.extending() ? 'Staying signed in…' : 'Stay signed in' }}
      </button>
    </div>
  </div>
}
